import React, { useCallback } from 'react';
import { View, Text, TextInput, Pressable, Platform } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { useTheme } from '../../../src/theme/useTheme';
import { useInviteModalStyles } from '../../../src/friends/contexts/InviteModalStylesContext';
import { ManualEmailItem } from './ManualEmailItem';
import { sanitizeEmailInput } from '../../../src/helpers/sanitizeInput.helper';

interface ManualTabContentProps {
  manualEmail: string;
  onManualEmailChange: (email: string) => void;
  onAddEmail: () => void;
  filteredManualEmails: string[];
  selectedContacts: Set<string>;
  onToggleContact: (email: string) => void;
  onRemoveEmail: (email: string) => void;
  testID?: string;
}

export const ManualTabContent: React.FC<ManualTabContentProps> = ({
  manualEmail,
  onManualEmailChange,
  onAddEmail,
  filteredManualEmails,
  selectedContacts,
  onToggleContact,
  onRemoveEmail,
  testID,
}) => {
  const theme = useTheme();
  const styles = useInviteModalStyles();
  const handleEmailChange = useCallback(
    (text: string) => {
      const sanitized = sanitizeEmailInput(text);
      onManualEmailChange(sanitized);
    },
    [onManualEmailChange]
  );

  return (
    <View testID={testID || 'manual-tab-content'}>
      <View style={styles.manualEmailRow}>
        <TextInput
          placeholder="Type email address..."
          placeholderTextColor={theme.gray[500]}
          value={manualEmail}
          onChangeText={handleEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          onSubmitEditing={onAddEmail}
          style={styles.manualEmailInput}
          testID={testID ? `${testID}-email-input` : 'manual-tab-email-input'}
        />
        <Pressable
          onPress={onAddEmail}
          style={({ pressed, hovered }: { pressed: boolean; hovered?: boolean }) => [
            styles.addButton,
            Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
            pressed && styles.addButtonPressed,
            hovered && Platform.OS === 'web' && styles.addButtonHover,
          ]}
          testID={testID ? `${testID}-add-button` : 'manual-tab-add-button'}
        >
          <Text style={styles.addButtonText}>Add</Text>
        </Pressable>
      </View>

      {filteredManualEmails.map((email) => (
        <ManualEmailItem
          key={email}
          email={email}
          isSelected={selectedContacts.has(email)}
          onToggle={() => onToggleContact(email)}
          onRemove={() => onRemoveEmail(email)}
        />
      ))}
    </View>
  );
};
